import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ruler } from 'lucide-react';
import { convertHeight, isPlausibleHeightCm } from '@/lib/measurement/units';

interface HeightInputProps {
  label?: string;
  value?: number; // height in cm (base unit)
  onChange: (cm: number) => void;
  unit?: 'cm' | 'in';
  allowUnitChange?: boolean;
  className?: string;
  placeholder?: string;
}

export const HeightInput: React.FC<HeightInputProps> = ({
  label,
  value = 0,
  onChange,
  unit = 'cm',
  allowUnitChange = true,
  className,
  placeholder
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [currentUnit, setCurrentUnit] = useState(unit);
  const [isUserTyping, setIsUserTyping] = useState(false);

  const cmToDisplayUnit = (cm: number, targetUnit: string): number => {
    return targetUnit === 'in' ? convertHeight(cm, 'cm', 'in') : cm;
  };

  const displayUnitToCm = (h: number, sourceUnit: string): number => {
    return sourceUnit === 'in' ? convertHeight(h, 'in', 'cm') : h;
  };

  useEffect(() => {
    if (!isUserTyping && value > 0) {
      const display = cmToDisplayUnit(value, currentUnit);
      setDisplayValue(display.toFixed(currentUnit === 'cm' ? 0 : 1));
    } else if (!isUserTyping && value === 0) {
      setDisplayValue('');
    }
  }, [value, currentUnit, isUserTyping]);

  const handleValueChange = (newValue: string) => {
    setIsUserTyping(true);
    setDisplayValue(newValue);
    const h = parseFloat(newValue);
    if (!isNaN(h) && h > 0) {
      const cm = displayUnitToCm(h, currentUnit);
      onChange(cm);
    } else if (newValue === '') {
      onChange(0);
    }
  };

  const handleBlur = () => {
    setIsUserTyping(false);
    const h = parseFloat(displayValue);
    if (!isNaN(h) && h > 0) {
      const formatted = h.toFixed(currentUnit === 'cm' ? 0 : 1);
      setDisplayValue(formatted);
    }
  };

  const handleUnitChange = (newUnit: string) => {
    const h = parseFloat(displayValue);
    if (!isNaN(h) && h > 0) {
      const cm = displayUnitToCm(h, currentUnit);
      const newDisplayValue = cmToDisplayUnit(cm, newUnit);
      setDisplayValue(newDisplayValue.toFixed(newUnit === 'cm' ? 0 : 1));
    }
    setCurrentUnit(newUnit as 'cm' | 'in');
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    return currentUnit === 'cm' ? '180' : '70.9';
  };

  const getValidationMessage = () => {
    const h = parseFloat(displayValue);
    if (displayValue && !isNaN(h)) {
      const cm = displayUnitToCm(h, currentUnit);
      if (!isPlausibleHeightCm(cm)) return 'Height seems outside typical range';
    }
    return null;
  };

  const validationMessage = getValidationMessage();

  return (
    <div className={className}>
      {label && <Label className="mb-2 block">{label}</Label>}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Ruler className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="number"
            value={displayValue}
            onChange={(e) => handleValueChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={getPlaceholder()}
            className="pl-10"
            step={currentUnit === 'cm' ? '1' : '0.1'}
            min="0"
          />
        </div>

        {allowUnitChange && (
          <Select value={currentUnit} onValueChange={handleUnitChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cm">cm</SelectItem>
              <SelectItem value="in">in</SelectItem>
            </SelectContent>
          </Select>
        )}

        {!allowUnitChange && (
          <div className="flex items-center justify-center w-12 text-sm text-muted-foreground">
            {currentUnit}
          </div>
        )}
      </div>

      {validationMessage && (
        <p className="mt-1 text-sm text-yellow-600">{validationMessage}</p>
      )}

      {value > 0 && currentUnit !== 'cm' && (
        <p className="mt-1 text-xs text-muted-foreground">{Math.round(value)} cm</p>
      )}
    </div>
  );
};
